import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscriptionBillingPolicy, cycleCurrentView, cyclePriceLabel, logicalCycleDates, cycleDateLabel,
} from '../../../utils/subscriptionBillingPolicy';
import { cycleRenewalTiming, MORNING_RENEWAL_TIMING } from './cycleRenewalTerms';

export const CycleUnavailable = () => {
  const { t } = useTranslation('global');
  return <span role="status">{t('lessonCycles.unavailable', { defaultValue: 'Dane cyklu są chwilowo niedostępne.' })}</span>;
};

export const CycleCadence = ({ record }) => {
  const { t } = useTranslation('global');
  const policy = subscriptionBillingPolicy(record);
  if (policy.kind !== 'lesson_cycle') return <CycleUnavailable />;
  return <span data-testid="cycle-cadence">{t('lessonCycles.weekly', {
    count: policy.lessonsPerWeek, defaultValue: '{{count}}× w tygodniu',
  })}</span>;
};

export const CyclePrice = ({ record, unitOnly = false }) => {
  const { t, i18n } = useTranslation('global');
  const policy = subscriptionBillingPolicy(record), price = cyclePriceLabel(record, i18n.language);
  if (policy.kind !== 'lesson_cycle' || !price) return <CycleUnavailable />;
  return <span data-testid="cycle-price">{!unitOnly ? <><strong>{price}</strong>{' '}</> : null}{t('lessonCycles.priceUnit', {
    count: policy.lessonsPerCycle, defaultValue: 'za cykl {{count}} lekcji',
  })}</span>;
};

export const CycleRenewalTerms = ({ record, consent = false }) => {
  const { t, i18n } = useTranslation('global');
  const policy = subscriptionBillingPolicy(record);
  if (policy.kind !== 'lesson_cycle') return <CycleUnavailable />;
  const timing = cycleRenewalTiming(record);
  if (!timing) return <CycleUnavailable />;
  if (timing === MORNING_RENEWAL_TIMING) {
    const english = String(i18n.language || '').toLowerCase().split('-')[0] === 'en';
    const rule = english
      ? 'We will charge you at 09:00 (Warsaw time) on the day of the last scheduled lesson. Once payment is confirmed, we will reserve the next lesson times.'
      : 'Opłata o 09:00 (czas warszawski) w dniu ostatniej zaplanowanej lekcji. Po potwierdzeniu płatności zarezerwujemy kolejne terminy.';
    return <span>{t(consent ? 'lessonCycles.morningRenewalConsent' : 'lessonCycles.morningRenewalRule', {
      defaultValue: consent ? (english ? 'I agree to charges for subsequent lesson cycles. ' : 'Zgadzam się na opłatę za kolejne cykle. ') + rule : rule,
    })}</span>;
  }
  return <span>{t(consent ? 'lessonCycles.consent' : 'lessonCycles.renewalRule', {
    count: policy.lessonsPerCycle, next: policy.lessonsPerCycle + 1,
    defaultValue: consent
      ? 'Zgadzam się na opłatę za kolejne cykle po {{count}} rozliczonych lekcjach, przed lekcją {{next}}, również w tym samym miesiącu. Same rezerwacje nie uruchamiają opłaty.'
      : 'Kolejna opłata po {{count}} rozliczonych lekcjach, przed lekcją {{next}}, również w tym samym miesiącu. Same rezerwacje nie uruchamiają opłaty.',
  })}</span>;
};

export const CycleDisableRenewalLabel = () => {
  const { t } = useTranslation('global');
  return <>{t('lessonCycles.disableRenewal', { defaultValue: 'Wyłącz odnawianie po bieżącej puli' })}</>;
};

export const CycleScheduleHelp = () => {
  const { t } = useTranslation('global');
  return <>{t('lessonCycles.scheduleHelp', { defaultValue: 'Stały rytm jest ciągły. Przed zmianą sprawdzimy dostępne terminy opłaconej puli.' })}</>;
};

export const CycleExpiryNote = () => {
  const { t } = useTranslation('global');
  return <>{t('lessonCycles.noCalendarExpiry', { defaultValue: 'Pula cyklu nie kończy się wraz z miesiącem.' })}</>;
};

export const CycleUsage = ({ record }) => {
  const { t, i18n } = useTranslation('global');
  const view = cycleCurrentView(record);
  if (!view) return <CycleUnavailable />;
  const renewalDisabled = record?.renewalEnabled === false || view.renewalEnabled === false || view.renewalStatus === 'disabled';
  const chargeTimezone = cycleRenewalTiming(record) === MORNING_RENEWAL_TIMING ? 'Europe/Warsaw' : record?.timezone;
  return <section className="lesson-cycle-usage" aria-live="polite" data-testid="cycle-usage">
    <p>{t('lessonCycles.progress', { used: view.billableUsed, count: view.lessonsPerCycle,
      reserved: view.reserved, available: view.available, granted: view.granted,
      defaultValue: 'Rozliczone: {{used}} / {{count}} · Przyznane: {{granted}} · Zarezerwowane: {{reserved}} · Do rezerwacji: {{available}}' })}</p>
    {renewalDisabled ? <p>{t('lessonCycles.renewalDisabled', {
      defaultValue: 'Odnowienie wyłączone. Zachowujesz opłacone lekcje.',
    })}</p> : <CycleRenewalTerms record={record} />}
    {['pending', 'processing', 'requires_action', 'failed', 'unknown', 'canceled'].includes(view.renewalStatus)
      ? <p role="status">{t('lessonCycles.renewalPending', { defaultValue: 'Płatność kolejnego cyklu wymaga potwierdzenia. Kolejne terminy nie są jeszcze opłacone.' })}</p> : null}
    {!renewalDisabled && typeof view.expectedChargeAt === 'string' && Number.isFinite(Date.parse(view.expectedChargeAt))
      ? <p>{t('lessonCycles.expectedCharge', { date: cycleDateLabel({ startTime: view.expectedChargeAt }, i18n.language, chargeTimezone),
        defaultValue: 'Przewidywane kolejne obciążenie: {{date}}' })}</p> : null}
  </section>;
};

// The caller supplies a server-owned funding category. No forecasts are made here.
export const CycleLessonDates = ({ slots, kind = 'held', timezone, record }) => {
  const { t, i18n } = useTranslation('global');
  const dates = logicalCycleDates(slots);
  if (!dates || !['held', 'paid', 'forecast'].includes(kind)) return <CycleUnavailable />;
  const key = { held: 'heldDates', paid: 'paidDates', forecast: 'forecastDates' }[kind];
  const fallback = {
    held: 'Terminy cyklu — oczekują na płatność',
    paid: 'Terminy opłaconego cyklu',
    forecast: 'Prognoza po odnowieniu — terminy nieopłacone i niezarezerwowane',
  }[kind];
  return <details className="lesson-cycle-dates" data-funding={kind}>
    <summary>{t('lessonCycles.' + key, { defaultValue: fallback })} ({dates.length})</summary>
    <ol>{dates.map((slot) => <li key={slot.startTime}>{cycleDateLabel(slot, i18n.language, timezone || slot.timezone || 'Europe/Warsaw')}</li>)}</ol>
    {Number(record?.lessonDurationMinutes || record?.planSnapshot?.lessonDurationMinutes) === 50
      ? <small>{t('lessonCycles.logical50', { defaultValue: 'Jedna lekcja: 25 + 5 przerwy + 25 minut, razem 55 minut w kalendarzu.' })}</small> : null}
  </details>;
};
